import cls from "classnames";
import { InputRef } from "antd";
import { Dropdown } from "antd";
import func from "@uikit/func";
import React, { useRef } from "react";
import { useDispatch } from "react-redux";
import { studioActions } from "@store/studio";
import { useClickAway, useCreation } from "ahooks";
import { AModal } from "@uiview/views/AModal/AModal";
import { TextAreaRef } from "antd/lib/input/TextArea";
import { useSignalEffect } from "@preact/signals-react";
import styles from "./PreviewHistoryListItem.module.scss";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import { PreviewMoreActions, updataType } from "./PreviewMoreActions";
import { PreviewHistoryListItemTitle } from "./PreviewHistoryListItemTitle";
import { PreviewHistoryListItemStatus } from "./PreviewHistoryListItemStatus";
import { PreviewHistoryListItemSubTitle } from "./PreviewHistoryListItemSubTitle";
import useObjectRelationGqlRequest from "@custom-hooks/useObjectRelationGqlRequest";
import { useHomeChildContainer } from "@custom-hooks/HomeUsePluginRoot/childContainer";
import { usePreviewAppStore } from "@custom-hooks/HomeUsePluginRoot/usePreviewAppStore";
import { PreviewHistoryListItemBloc, PreviewHistoryListBlocState } from "./PreviewHistoryListItemBloc";
import { logEvent } from "@uikit/service/amplitude";

export interface PreviewHistoryListItemProps {
  item: PreviewApp.UISaasValueType;
  isCodeLodaing: boolean;
  selectPreview: (previewData: any) => void;
  handleCopyLink: (link: string, uuid: string) => void;
  handleQrcode: (link: string) => void;
}

export function PreviewHistoryListItem(props: PreviewHistoryListItemProps) {
  const { id, v2App } = props.item;
  const { handleCopyLink, handleQrcode, isCodeLodaing } = props;

  const state = new PreviewHistoryListBlocState();
  const bloc = useCreation(() => new PreviewHistoryListItemBloc(state), []);

  const dispatch = useDispatch();
  const { gql } = useObjectRelationGqlRequest();
  const { handleSave } = useHomeChildContainer();
  const { updatePreviewV2App, deletePreviewV2App, getPublishMetadata, updatedv2AppData } = usePreviewAppStore();
  const setSelectedTemplate = (val: any) => {
    dispatch(studioActions.setSelectedTemplate(val));
  };

  const textWrapperRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<InputRef>(null);
  const subTitleRef = useRef<TextAreaRef>(null);

  const link = getPublishMetadata(id)?.link;
  const uuid = getPublishMetadata(id)?.uuid ?? "";
  const isPublished = !func.isEmpty(link);

  const clickDeleteOk = async () => {
    try {
      bloc.state.saveLoading.value = true;
      // 删除本地数据
      deletePreviewV2App(id);
      // 调用接口更新数据
      if (isPublished) {
        await handleSave({ background: true });
      }
      func.customMsg({
        content: "successfully",
        type: "success",
      });
      logEvent("delete_publish_view", bloc.getDeleteEventProps(props.item, uuid, link));
    } catch (error) {
      func.messageError(error);
    } finally {
      bloc.state.saveLoading.value = false;
      bloc.clickDeleteCancel();
    }
  };
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowUp") {
      setTimeout(() => {
        titleRef.current?.focus({
          cursor: "end",
        });
      });
    } else if (e.key === "ArrowDown") {
      subTitleRef.current?.focus({
        cursor: "end",
      });
    }
  }

  useSignalEffect(() => {
    if (bloc.state.isEditing.value) {
      textWrapperRef.current && textWrapperRef.current.addEventListener("keydown", onKeyDown);
    }
    return () => {
      textWrapperRef.current && textWrapperRef.current.removeEventListener("keydown", onKeyDown);
    };
  });

  const handleOk = async (data: updataType) => {
    try {
      bloc.state.confirmLoading.value = true;
      // 修改本地数据
      updatePreviewV2App(id, data);
      // 调用接口更新数据
      if (isPublished) {
        const updatedProject = updatedv2AppData();
        await gql.mutation.updateObject(
          parseInt(updatedProject.id),
          {
            name: "studio_project",
            attributes: updatedProject,
          },
          ""
        );
        setSelectedTemplate(updatedProject);
      }
      func.customMsg({
        content: "successfully",
        type: "success",
      });
    } catch (error) {
      func.messageError(error);
    } finally {
      bloc.state.confirmLoading.value = false;
      bloc.handleCancel();
    }
  };

  function onCopyLinkClick(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (!link) {
      func.customMsg({
        content: "Link cannot be empty!",
        type: "info",
      });
      return;
    }
    handleCopyLink(link, uuid);
    bloc.onClickClose();
  }

  const dropdownProps = (): any => {
    return document.getElementById("homeNavBasssr");
  };

  useClickAway(() => {
    bloc.onCloseEditing();
  }, textWrapperRef);

  const title = `Are you sure you want to Delete this Publish View?`;
  const description = `This will delete this publish view from your project. You cannot undo this action.`;
  const discardBtnText = "Cancel";
  const saveBtnText = "Delete Publish View";
  const buttons = [discardBtnText, saveBtnText];

  return (
    <div>
      <div
        className={styles.item}
        onClick={() => {
          if (bloc.state.isEditing.value) {
            return;
          }

          props.selectPreview(props.item);
        }}
      >
        <div className={styles.content}>
          <div ref={textWrapperRef} className={styles.textWrapper} onClick={e => bloc.onTextClick(e)}>
            <PreviewHistoryListItemTitle
              ref={titleRef}
              value={v2App?.appName || ""}
              isEditing={bloc.state.isEditing.value}
              handleEditing={val => (bloc.state.isEditing.value = val)}
              handleUpdateTitle={title => {
                updatePreviewV2App(id, {
                  appName: title,
                });
              }}
            />
            <PreviewHistoryListItemSubTitle
              ref={subTitleRef}
              value={v2App?.appDescription || ""}
              isEditing={bloc.state.isEditing.value}
              handleEditing={val => (bloc.state.isEditing.value = val)}
              handleUpdateDesc={desc => {
                updatePreviewV2App(id, {
                  appDescription: desc,
                });
              }}
            />
          </div>
          <div className={styles.arrow}>
            <img
              className={styles.right_icon}
              src={`${settings.S3CDN}${settings.viewAssetsPath}dropdown-right.svg`}
              alt="icon"
            />
            {/* <PreviewHistoryArrow /> */}
          </div>
        </div>

        <Dropdown
          open={bloc.state.isDropdownModalOpen.value}
          onOpenChange={bloc.clickisDropdownModalOpenCancel}
          trigger={["click"]}
          placement="bottomRight"
          getPopupContainer={() => dropdownProps()}
          overlay={
            <div className={styles.moreActionsBox}>
              <div className={styles.boxItem} onClick={e => onCopyLinkClick(e)}>
                Copy Share Link
              </div>
              <div className={styles.boxItem} onClick={e => bloc.clickDotsEdit(e)}>
                Edit Detail
              </div>
              <div className={cls(styles.boxItem, styles.deleteItem)} onClick={e => bloc.clickDotsDelete(e)}>
                Delete this Publish View
              </div>
            </div>
          }
        >
          <div className={styles.previewHistory} id="homeNavBasssr">
            <PreviewHistoryListItemStatus
              id={id}
              handleCopyLink={link => handleCopyLink(link, uuid)}
              handleQrcode={link => handleQrcode(link)}
              isCodeLodaing={isCodeLodaing}
              clickisDropdownModalOpen={bloc.clickisDropdownModalOpen}
            />
          </div>
        </Dropdown>
      </div>

      <PreviewMoreActions
        handleOk={handleOk}
        handleCancel={() => bloc.handleCancel()}
        isModalOpen={bloc.state.isModalOpen.value}
        confirmLoading={bloc.state.confirmLoading.value}
        v2App={v2App}
      />

      <AModal
        className={styles.deletePopup}
        open={bloc.state.backHomeModal.value}
        title={title}
        subTitle={description}
        loading={bloc.state.saveLoading.value}
        buttons={buttons}
        shouldExecuteOkOnEnter={true}
        onCancel={bloc.clickDeleteCancel}
        onOk={clickDeleteOk}
      ></AModal>
    </div>
  );
}
